import * as THREE from 'three';
import { HUSTLES, hustleDef, hustleOpen, type HustleId } from '../content/hustles';
import type { GameState } from '../state/types';
import { LOTS, SPOTS, type Vec2 } from '../world/layout';
import { roomSlotAt } from '../world/rooms';
import type { GameServices } from './services';

/**
 * The hustle ladder's bookkeeping: tells you when a new way to make money opens up, and
 * points the way when you ask your phone to "show me."
 */
export class Hustles {
  private pin: { id: HustleId; until: number } | null = null;
  private checking = false;
  private readonly tmp = new THREE.Vector3();

  constructor(private readonly s: GameServices) {
    s.store.bus.on('change', () => this.check());
  }

  /** Newly unlocked hustles get a fanfare (once each). */
  check(): void {
    if (this.checking) return;
    this.checking = true;
    try {
      const { store, ui } = this.s;
      for (const h of HUSTLES) {
        if (!h.needs.length || store.flag(`hustle_${h.id}`) || !hustleOpen(store.state, h.id)) continue;
        store.setFlag(`hustle_${h.id}`, true);
        this.s.sfx('airhorn');
        ui.hud.unlockPop(':briefcase: NEW HUSTLE UNLOCKED', `${h.icon} ${h.name}`, h.how);
      }
    } finally {
      this.checking = false;
    }
  }

  /** Don't make a fuss about hustles that were already open in a save you're loading. */
  static quiet(state: GameState): void {
    for (const h of HUSTLES)
      if (h.needs.length && hustleOpen(state, h.id)) state.flags[`hustle_${h.id}`] = true;
  }

  /** Where a hustle starts. */
  where(id: HustleId): Vec2 | null {
    const pin = hustleDef(id).pin;
    if (!pin) return null;
    if (pin === 'home') return this.s.home().washSign;
    if (pin === 'lot10') {
      const stage = this.s.store.flag('rental_stage');
      return !stage || stage === 'none' ? SPOTS.office : { x: LOTS.vacant.trailer.x - 6, z: LOTS.vacant.z };
    }
    return SPOTS[pin];
  }

  /** "Show me": the objective arrow points there for a while. */
  show(id: HustleId): void {
    this.pin = { id, until: this.s.now() + 240 };
    this.s.store.toast(`Follow the arrow to start: ${hustleDef(id).name}.`, 'info', hustleDef(id).icon);
  }

  objective(): { text: string; target: THREE.Vector3 } | null {
    const pin = this.pin;
    if (!pin) return null;
    const at = this.where(pin.id);
    const p = this.s.player.position;
    if (!at || this.s.now() > pin.until || roomSlotAt(p.x) >= 0) {
      if (this.s.now() > pin.until) this.pin = null;
      return null;
    }
    if (Math.hypot(at.x - p.x, at.z - p.z) < 3) {
      this.pin = null;
      return null;
    }
    const h = hustleDef(pin.id);
    return { text: `${h.icon} ${h.name}`, target: this.tmp.set(at.x, 0, at.z) };
  }
}
